/**
 * UIScene — game-agnostic top-bar UI overlay.
 *
 * Provides a pause button + settings dropdown (sound toggle, fullscreen toggle)
 * that floats above GameScene without blocking input on the rest of the screen.
 *
 * Usage from GameScene:
 *   this.scene.launch('UIScene');
 *
 * Required assets (loaded inside this scene's preload):
 *   - paused.png            (full-screen "Paused" overlay shown while paused)
 *   - mask.png              (graphic that clips the settings dropdown so it
 *                            appears to slide down from behind the top bar)
 *   - dropDown.png          (background of the dropdown column)
 *   - circle.png            (settings ring / interactive hit-area)
 *   - yellow.png            (yellow circle behind the settings ring)
 *   - sound.png / mute.png  (sound toggle textures)
 *   - fullScreen.png / fullScreenOff.png   (fullscreen toggle textures)
 *   - playPauseAnim multiatlas (pause<->play animation)
 *
 * All assets must live under `assets/GameUIAnims/`.
 */
export default class UIScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIScene' });
    this.isPaused = false;
    this.isSettingsOpen = false;
    this.userInteracted = false;
    this.autoCollapseCalls = 0;
  }

  preload() {
    const base = 'assets/GameUIAnims';
    this.load.image('ui-paused', `${base}/paused.png`);
    this.load.image('ui-mask', `${base}/mask.png`);
    this.load.image('ui-dropDown', `${base}/dropDown.png`);
    this.load.image('ui-circle', `${base}/circle.png`);
    this.load.image('ui-yellow', `${base}/yellow.png`);
    this.load.image('ui-sound', `${base}/sound.png`);
    this.load.image('ui-mute', `${base}/mute.png`);
    this.load.image('ui-fullScreen', `${base}/fullScreen.png`);
    this.load.image('ui-fullScreenOff', `${base}/fullScreenOff.png`);
    this.load.multiatlas('ui-playAnim', `${base}/playPauseAnim.json`, base);
  }

  create() {
    this.isPaused = false;
    this.isSettingsOpen = false;
    this.userInteracted = false;
    this.autoCollapseCalls = 0;

    this.createPausedOverlay();
    this.createSettingsDropdown();
    this.createTopRightControls();

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
  }

  // ==================== Layout pieces ====================

  createPausedOverlay() {
    this.pausedOverlay = this.add
      .image(0, 0, 'ui-paused')
      .setOrigin(0)
      .setDepth(10)
      .setVisible(false);
  }

  createSettingsDropdown() {
    // Mask so the dropdown appears to slide down from behind the top bar.
    const maskShape = this.make.graphics();
    maskShape.beginPath();
    maskShape.fillRect(0, 75, 1280, 150);
    const dropdownMask = maskShape.createGeometryMask();

    this.dropDownBg = this.add.image(1220, 145, 'ui-dropDown').setOrigin(0.5, 0.5);
    this.soundBtn = this.add
      .image(1220, 132, 'ui-sound')
      .setOrigin(0.5, 0.5)
      .setDepth(2)
      .setScale(0.75)
      .setInteractive({ cursor: 'pointer' });
    this.fullScreenBtn = this.add
      .image(1220, 185, 'ui-fullScreenOff')
      .setOrigin(0.5, 0.5)
      .setDepth(2)
      .setScale(0.73)
      .setInteractive({ cursor: 'pointer' });

    this.dropdownContainer = this.add
      .container(0, -130, [this.dropDownBg, this.soundBtn, this.fullScreenBtn])
      .setDepth(2);
    this.dropdownContainer.setMask(dropdownMask);

    this.soundBtn.on('pointerup', () => this.toggleSound());
    this.fullScreenBtn.on('pointerup', () => this.toggleFullscreen());
  }

  createTopRightControls() {
    this.settingsYellow = this.add
      .image(1220, 72, 'ui-yellow')
      .setDepth(2)
      .setOrigin(0.5, 0.5);
    this.settingsRing = this.add
      .image(1220, 72, 'ui-circle')
      .setDepth(2)
      .setOrigin(0.5, 0.5)
      .setInteractive({ cursor: 'pointer' });

    this.anims.create({
      key: 'ui-pause-idle',
      frames: this.anims.generateFrameNames('ui-playAnim', {
        start: 21,
        end: 21,
        prefix: 'playPause',
        suffix: '.png',
      }),
      frameRate: 30,
      repeat: 0,
    });
    this.anims.create({
      key: 'ui-pause-toplay',
      frames: this.anims.generateFrameNames('ui-playAnim', {
        start: 21,
        end: 39,
        prefix: 'playPause',
        suffix: '.png',
      }),
      frameRate: 30,
      repeat: 0,
    });

    this.pauseBtn = this.add
      .sprite(1125, 72, 'ui-playAnim')
      .setDepth(2)
      .setInteractive({ cursor: 'pointer', pixelPerfect: true })
      .play('ui-pause-idle');

    this.pauseBtn.on('pointerup', () => this.togglePause());
    this.settingsRing.on('pointerup', () => this.toggleSettings());
  }

  // ==================== Pause behaviour ====================

  togglePause() {
    if (!this.isPaused) {
      this.pauseBtn.anims.play('ui-pause-toplay');
      this.pauseAllScenes();
      this.game.sound.pauseAll();
      this.pausedOverlay.setVisible(true);
      this.isPaused = true;
    } else {
      this.resumeAllScenes();
      this.game.sound.resumeAll();
      this.pauseBtn.anims.playReverse('ui-pause-toplay', true);
      this.pausedOverlay.setVisible(false);
      this.isPaused = false;
    }
  }

  pauseAllScenes() {
    this.scene.manager.scenes.forEach(scene => {
      if (scene.scene.isActive() && scene.scene.key !== 'UIScene') {
        scene.scene.pause();
      }
    });
  }

  resumeAllScenes() {
    this.scene.manager.scenes.forEach(scene => {
      if (scene.scene.isPaused() && scene.scene.key !== 'UIScene') {
        scene.scene.resume();
      }
    });
  }

  // ==================== Settings dropdown ====================

  toggleSettings() {
    this.isSettingsOpen = !this.isSettingsOpen;
    if (this.dropdownContainer.y === -130) {
      this.tweenButton(this.settingsYellow, 0.9, 200);
      this.spinRing(60);
      this.time.delayedCall(200, () => this.slideDropdown(0));
    } else {
      this.tweenButton(this.settingsYellow, 0.97, 75);
      this.tweenButton(this.dropDownBg, 0.97, 75);
      this.spinRing(-60);
      this.time.delayedCall(200, () => this.slideDropdown(-130));
    }
  }

  slideDropdown(y) {
    this.tweens.add({
      targets: this.dropdownContainer,
      y,
      ease: 'Linear',
      duration: 500,
      delay: 100,
      onComplete: () => {
        if (y === 0) this.autoCollapseCalls = 0;
      },
    });
  }

  scheduleAutoCollapse() {
    this.time.delayedCall(5000, () => {
      if (!this.userInteracted) this.slideDropdown(-130);
      else this.autoCollapseCalls = 0;
    });
  }

  tweenButton(target, scale, duration) {
    this.tweens.add({
      targets: target,
      scale,
      ease: 'Sine.easeInOut',
      duration,
      yoyo: true,
    });
  }

  spinRing(angle) {
    this.tweens.add({
      targets: [this.settingsRing],
      x: 1219,
      y: 70,
      ease: 'Sine.easeInOut',
      duration: 75,
      delay: 10,
      yoyo: true,
      onComplete: () => {
        this.tweens.add({
          targets: [this.settingsRing],
          angle,
          ease: 'Power1',
          duration: 500,
          delay: 25,
        });
      },
    });
  }

  // ==================== Sound + fullscreen toggles ====================

  toggleSound() {
    this.userInteracted = true;
    this.tweenButton(this.soundBtn, 0.85, 75);
    const muted = this.game.sound.mute;
    this.game.sound.mute = !muted;
    this.soundBtn.setTexture(this.game.sound.mute ? 'ui-mute' : 'ui-sound');
  }

  toggleFullscreen() {
    this.userInteracted = true;
    this.tweenButton(this.fullScreenBtn, 0.83, 75);
    if (this.scale.isFullscreen) {
      this.scale.stopFullscreen();
      this.fullScreenBtn.setTexture('ui-fullScreen');
    } else {
      this.scale.startFullscreen();
      this.fullScreenBtn.setTexture('ui-fullScreenOff');
    }
  }

  // ==================== Update + cleanup ====================

  update() {
    if (
      this.dropdownContainer &&
      this.dropdownContainer.y === 0 &&
      this.autoCollapseCalls === 0
    ) {
      this.autoCollapseCalls++;
      this.userInteracted = false;
      this.scheduleAutoCollapse();
    }
  }

  onShutdown() {
    this.events.off(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
  }
}
